import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function ChildrenPage() {
  const { t } = useTranslation('common');
  const [selectedActivity, setSelectedActivity] = useState<string | null>(null);
  const [chatMessage, setChatMessage] = useState('');
  const [newPost, setNewPost] = useState('');
  const [showComments, setShowComments] = useState<{ [key: number]: boolean }>({});

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  const activities = {
    drawing: {
      title: t('children_activity_drawing'),
      icon: 'ri-pencil-line',
      color: 'yellow',
      bgColor: 'bg-yellow-900/30',
      textColor: 'text-yellow-400',
      gradientFrom: 'from-yellow-400',
      gradientTo: 'to-orange-400',
      description: t('children_activity_drawing_full'),
      participants: t('children_activity_drawing_participants'),
      features: [
        t('children_activity_drawing_feature1'),
        t('children_activity_drawing_feature2'),
        t('children_activity_drawing_feature3'),
        t('children_activity_drawing_feature4'),
        t('children_activity_drawing_feature5'),
      ],
    },
    music: {
      title: t('children_activity_music'),
      icon: 'ri-music-line',
      color: 'orange',
      bgColor: 'bg-orange-900/30',
      textColor: 'text-orange-400',
      gradientFrom: 'from-orange-400',
      gradientTo: 'to-red-400',
      description: t('children_activity_music_full'),
      participants: t('children_activity_music_participants'),
      features: [
        t('children_activity_music_feature1'),
        t('children_activity_music_feature2'),
        t('children_activity_music_feature3'),
        t('children_activity_music_feature4'),
        t('children_activity_music_feature5'),
      ],
    },
    stories: {
      title: t('children_activity_stories'),
      icon: 'ri-book-read-line',
      color: 'green',
      bgColor: 'bg-green-900/30',
      textColor: 'text-green-400',
      gradientFrom: 'from-green-400',
      gradientTo: 'to-teal-400',
      description: t('children_activity_stories_full'),
      participants: t('children_activity_stories_participants'),
      features: [
        t('children_activity_stories_feature1'),
        t('children_activity_stories_feature2'),
        t('children_activity_stories_feature3'),
        t('children_activity_stories_feature4'),
        t('children_activity_stories_feature5'),
      ],
    },
    puzzles: {
      title: t('children_activity_puzzles'),
      icon: 'ri-puzzle-line',
      color: 'blue',
      bgColor: 'bg-cyan-900/30',
      textColor: 'text-cyan-400',
      gradientFrom: 'from-cyan-400',
      gradientTo: 'to-teal-400',
      description: t('children_activity_puzzles_full'),
      participants: t('children_activity_puzzles_participants'),
      features: [
        t('children_activity_puzzles_feature1'),
        t('children_activity_puzzles_feature2'),
        t('children_activity_puzzles_feature3'),
        t('children_activity_puzzles_feature4'),
        t('children_activity_puzzles_feature5'),
      ],
    },
    science: {
      title: t('children_activity_science'),
      icon: 'ri-flask-line',
      color: 'purple',
      bgColor: 'bg-fuchsia-900/30',
      textColor: 'text-fuchsia-400',
      gradientFrom: 'from-fuchsia-400',
      gradientTo: 'to-pink-400',
      description: t('children_activity_science_full'),
      participants: t('children_activity_science_participants'),
      features: [
        t('children_activity_science_feature1'),
        t('children_activity_science_feature2'),
        t('children_activity_science_feature3'),
        t('children_activity_science_feature4'),
        t('children_activity_science_feature5'),
      ],
    },
    sports: {
      title: t('children_activity_sports'),
      icon: 'ri-basketball-line',
      color: 'red',
      bgColor: 'bg-red-900/30',
      textColor: 'text-red-400',
      gradientFrom: 'from-red-400',
      gradientTo: 'to-orange-400',
      description: t('children_activity_sports_full'),
      participants: t('children_activity_sports_participants'),
      features: [
        t('children_activity_sports_feature1'),
        t('children_activity_sports_feature2'),
        t('children_activity_sports_feature3'),
        t('children_activity_sports_feature4'),
        t('children_activity_sports_feature5'),
      ],
    },
    coding: {
      title: t('children_activity_coding'),
      icon: 'ri-code-line',
      color: 'teal',
      bgColor: 'bg-teal-900/30',
      textColor: 'text-teal-400',
      gradientFrom: 'from-teal-400',
      gradientTo: 'to-cyan-400',
      description: t('children_activity_coding_full'),
      participants: t('children_activity_coding_participants'),
      features: [
        t('children_activity_coding_feature1'),
        t('children_activity_coding_feature2'),
        t('children_activity_coding_feature3'),
        t('children_activity_coding_feature4'),
        t('children_activity_coding_feature5'),
      ],
    },
    nature: {
      title: t('children_activity_nature'),
      icon: 'ri-leaf-line',
      color: 'green',
      bgColor: 'bg-emerald-900/30',
      textColor: 'text-emerald-400',
      gradientFrom: 'from-emerald-400',
      gradientTo: 'to-green-400',
      description: t('children_activity_nature_full'),
      participants: t('children_activity_nature_participants'),
      features: [
        t('children_activity_nature_feature1'),
        t('children_activity_nature_feature2'),
        t('children_activity_nature_feature3'),
        t('children_activity_nature_feature4'),
        t('children_activity_nature_feature5'),
      ],
    },
  };

  const ActivityModal = ({ activityKey }: { activityKey: string }) => {
    const activity = activities[activityKey as keyof typeof activities];
    if (!activity) return null;

    return (
      <div 
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-6"
        onClick={() => setSelectedActivity(null)}
      >
        <div 
          className="bg-gray-900 border border-gray-800 rounded-3xl max-w-2xl w-full p-8 relative"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => setSelectedActivity(null)}
            className="absolute top-6 right-6 w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-gray-700 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl text-gray-300"></i>
          </button>

          <div className={`w-20 h-20 ${activity.bgColor} rounded-full flex items-center justify-center mb-6`}>
            <i className={`${activity.icon} text-3xl ${activity.textColor}`}></i>
          </div>

          <h2 className="text-3xl font-bold mb-4 text-white">{activity.title}</h2>
          
          <div className="flex items-center space-x-4 mb-6">
            <div className="flex items-center space-x-2">
              <i className="ri-user-line text-gray-400"></i>
              <span className="text-gray-300 font-semibold">{activity.participants}</span>
            </div>
            <div className={`px-3 py-1 ${activity.bgColor} ${activity.textColor} rounded-full text-sm font-semibold`}>
              {t('children_modal_safe_badge')}
            </div>
          </div>

          <p className="text-gray-300 text-lg leading-relaxed mb-6">
            {activity.description}
          </p>

          <div className="mb-6">
            <h3 className="text-lg font-bold mb-3 text-white">{t('children_modal_what_you_can_do')}</h3>
            <div className="flex flex-wrap gap-2">
              {activity.features.map((feature, index) => (
                <span 
                  key={index}
                  className="px-4 py-2 bg-gray-800 text-gray-300 rounded-full text-sm font-medium"
                >
                  {feature}
                </span>
              ))}
            </div>
          </div>

          <button
            className={`w-full bg-gradient-to-r ${activity.gradientFrom} ${activity.gradientTo} text-black py-4 rounded-xl font-bold text-lg hover:scale-102 transition-all shadow-lg whitespace-nowrap cursor-pointer flex items-center justify-center space-x-2`}
          >
            <i className="ri-play-line"></i>
            <span>{t('children_modal_start_btn')}</span>
          </button>
        </div>
      </div>
    );
  };

  // Mock posts data
  const posts = [
    {
      id: 1,
      author: 'Little Minh',
      avatar: 'https://public.readdy.ai/ai/img_res/1d5f4cbe2b8d690a3e36ac7d9dd0fedb.jpg',
      time: '5 minutes ago',
      content: 'Today I drew a beautiful butterfly! 🦋🎨',
      image: 'https://readdy.ai/api/search-image?query=beautiful%20colorful%20butterfly%20drawing%20made%20by%20child%20with%20crayons%20vibrant%20colors%20simple%20background%20cheerful%20art%20style%20kids%20artwork&width=600&height=400&seq=child-post-001&orientation=landscape',
      likes: 24,
      comments: 8,
    },
    {
      id: 2,
      author: 'Little Huong',
      avatar: 'https://readdy.ai/api/search-image?query=adorable%20smiling%20girl%20child%20avatar%20cartoon%20style%20with%20pigtails%20colorful%20background%20happy%20expression%20bright%20cheerful&width=100&height=100&seq=child-avatar-002&orientation=squarish',
      time: '15 minutes ago',
      content: 'I just finished reading Little Red Riding Hood! So interesting! 📚✨',
      likes: 18,
      comments: 5,
    },
    {
      id: 3,
      author: 'Little Tuan',
      avatar: 'https://readdy.ai/api/search-image?query=cute%20boy%20child%20avatar%20smiling%20cartoon%20style%20with%20cap%20colorful%20background%20cheerful%20happy%20expression%20bright%20colors&width=100&height=100&seq=child-avatar-003&orientation=squarish',
      time: '30 minutes ago',
      content: 'Playing puzzle games with friends is so fun! 🎮🎉',
      image: 'https://readdy.ai/api/search-image?query=colorful%20puzzle%20game%20pieces%20on%20table%20children%20playing%20together%20bright%20cheerful%20scene%20simple%20clean%20background%20happy%20atmosphere%20kids%20activity&width=600&height=400&seq=child-post-002&orientation=landscape',
      likes: 32,
      comments: 12,
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-pink-50 to-purple-50 relative overflow-hidden">
      {/* Background Image */}
      <div 
        className="fixed inset-0 bg-cover bg-center z-0"
        style={{
          backgroundImage: 'url(https://readdy.ai/api/search-image?query=Colorful%20playground%20with%20rainbow%20slides%2C%20balloons%2C%20butterflies%2C%20and%20happy%20children%20playing%20in%20a%20magical%20garden%20with%20bright%20sunshine%2C%20flowers%2C%20and%20toys%20scattered%20around%2C%20vibrant%20colors%2C%20joyful%20atmosphere%2C%20cartoon%20style%2C%20cheerful%20and%20energetic%20scene&width=1920&height=1080&seq=children-bg-001&orientation=landscape)'
        }}
      />

      {/* Floating Elements */}
      <div className="absolute top-20 left-10 w-16 h-16 bg-yellow-400 rounded-full opacity-60 animate-bounce z-10"></div>
      <div className="absolute top-40 right-20 w-12 h-12 bg-pink-400 rounded-full opacity-60 animate-pulse z-10"></div>
      <div className="absolute bottom-32 left-1/4 w-20 h-20 bg-purple-400 rounded-full opacity-50 animate-bounce z-10" style={{ animationDelay: '1s' }}></div>
      <div className="absolute bottom-20 right-1/3 w-14 h-14 bg-orange-400 rounded-full opacity-60 animate-pulse z-10" style={{ animationDelay: '0.5s' }}></div>

      {/* Navigation */}
      <nav className="relative z-20 bg-white/95 backdrop-blur-sm shadow-lg">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <img 
                src="https://static.readdy.ai/image/a79563fe53efb1b307a7324e5db6bc2e/126dd8573ffd64afbaee10d6ee258da0.jpeg"
                alt="FaceTrue Logo" 
                className="w-10 h-10 rounded-full"
              />
              <span className="text-xl font-bold text-gray-800">FaceTrue Kids</span>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a 
                href="#" 
                onClick={(e) => { e.preventDefault(); scrollToSection('features'); }} 
                className="text-gray-600 hover:text-yellow-500 transition-colors text-sm font-medium cursor-pointer no-underline"
              >
                {t('children_nav_features')}
              </a>
              <a 
                href="#" 
                onClick={(e) => { e.preventDefault(); scrollToSection('safety'); }} 
                className="text-gray-600 hover:text-yellow-500 transition-colors text-sm font-medium cursor-pointer no-underline"
              >
                {t('children_nav_safety')}
              </a>
              <a 
                href="#" 
                onClick={(e) => { e.preventDefault(); scrollToSection('activities'); }} 
                className="text-gray-600 hover:text-yellow-500 transition-colors text-sm font-medium cursor-pointer no-underline"
              >
                {t('children_nav_activities')}
              </a>
              <a 
                href="/video-children"
                className="bg-gradient-to-r from-pink-500 to-orange-500 text-white px-5 py-2 rounded-full font-bold hover:scale-105 transition-all whitespace-nowrap cursor-pointer no-underline flex items-center space-x-2 shadow-lg"
              >
                <i className="ri-video-line"></i>
                <span>🎬 VIDEO</span>
              </a>
            </div>
            <a 
              href="/"
              className="bg-yellow-400 text-black px-6 py-2 rounded-full font-semibold hover:bg-yellow-500 transition-all whitespace-nowrap cursor-pointer no-underline"
            >
              {t('children_back_home')}
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="min-h-screen flex items-center justify-center relative overflow-hidden pt-20 z-10">
        
        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          <div className="w-32 h-32 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mb-8 mx-auto shadow-2xl shadow-yellow-500/50 animate-bounce-slow">
            <i className="ri-emotion-happy-line text-6xl text-white"></i>
          </div>
          
          <h1 className="text-6xl md:text-8xl font-black mb-8 leading-tight drop-shadow-2xl">
            <span className="text-white">
              {t('children_hero_title')}
            </span>
            <br />
            <span className="text-blue-500">
              {t('children_hero_subtitle')}
            </span>
          </h1>

          <p className="text-xl md:text-2xl text-white mb-12 leading-relaxed max-w-4xl mx-auto font-semibold" dangerouslySetInnerHTML={{ __html: t('children_hero_desc') }} />
        </div>
      </section>

      {/* Safety Features */}
      <section className="py-20 bg-white/80 backdrop-blur-sm relative z-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16 animate-fade-in-up">
            <h2 className="text-4xl md:text-5xl font-black mb-6 text-gray-800">
              <strong>{t('children_safety_title')}</strong>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-semibold">
              {t('children_safety_subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Parent Control */}
            <div className="bg-white rounded-3xl p-8 border-2 border-blue-300 hover:border-blue-400 hover:shadow-xl transition-all duration-300 animate-fade-in-up cursor-pointer hover:scale-105">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-400 to-cyan-400 rounded-2xl flex items-center justify-center mb-6 mx-auto shadow-lg">
                <i className="ri-parent-line text-4xl text-white"></i>
              </div>
              <h3 className="text-2xl font-black mb-4 text-center text-gray-800">
                <strong>{t('children_safety1_title')}</strong>
              </h3>
              <p className="text-gray-600 text-center font-medium leading-relaxed">
                {t('children_safety1_desc')}
              </p>
            </div>

            {/* Content Filter */}
            <div className="bg-white rounded-3xl p-8 border-2 border-green-300 hover:border-green-400 hover:shadow-xl transition-all duration-300 animate-fade-in-up animation-delay-200 cursor-pointer hover:scale-105">
              <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-400 rounded-2xl flex items-center justify-center mb-6 mx-auto shadow-lg">
                <i className="ri-shield-check-line text-4xl text-white"></i>
              </div>
              <h3 className="text-2xl font-black mb-4 text-center text-gray-800">
                <strong>{t('children_safety2_title')}</strong>
              </h3>
              <p className="text-gray-600 text-center font-medium leading-relaxed">
                {t('children_safety2_desc')}
              </p>
            </div>

            {/* Time Limit */}
            <div className="bg-white rounded-3xl p-8 border-2 border-purple-300 hover:border-purple-400 hover:shadow-xl transition-all duration-300 animate-fade-in-up animation-delay-400 cursor-pointer hover:scale-105">
              <div className="w-20 h-20 bg-gradient-to-br from-purple-400 to-pink-400 rounded-2xl flex items-center justify-center mb-6 mx-auto shadow-lg">
                <i className="ri-time-line text-4xl text-white"></i>
              </div>
              <h3 className="text-2xl font-black mb-4 text-center text-gray-800">
                <strong>{t('children_safety3_title')}</strong>
              </h3>
              <p className="text-gray-600 text-center font-medium leading-relaxed">
                {t('children_safety3_desc')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Fun Activities */}
      <section id="activities" className="py-20 bg-white/70 backdrop-blur-sm relative z-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16 animate-fade-in-up">
            <h2 className="text-4xl md:text-5xl font-black mb-6 text-gray-800">
              <strong>{t('children_activities_title')}</strong>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-semibold">
              {t('children_activities_subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Activity 1 */}
            <div className="bg-white rounded-2xl p-6 border-2 border-pink-300 hover:border-pink-400 hover:shadow-xl transition-all duration-300 cursor-pointer hover:scale-105 animate-fade-in-up">
              <div className="w-16 h-16 bg-gradient-to-br from-pink-400 to-rose-400 rounded-xl flex items-center justify-center mb-4 mx-auto shadow-md">
                <i className="ri-palette-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-black mb-3 text-center text-gray-800">
                <strong>{t('activity1_title')}</strong>
              </h3>
              <p className="text-gray-600 text-center text-sm font-medium">
                {t('activity1_desc')}
              </p>
            </div>

            {/* Activity 2 */}
            <div className="bg-white rounded-2xl p-6 border-2 border-blue-300 hover:border-blue-400 hover:shadow-xl transition-all duration-300 cursor-pointer hover:scale-105 animate-fade-in-up animation-delay-200">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-cyan-400 rounded-xl flex items-center justify-center mb-4 mx-auto shadow-md">
                <i className="ri-gamepad-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-black mb-3 text-center text-gray-800">
                <strong>{t('activity2_title')}</strong>
              </h3>
              <p className="text-gray-600 text-center text-sm font-medium">
                {t('activity2_desc')}
              </p>
            </div>

            {/* Activity 3 */}
            <div className="bg-white rounded-2xl p-6 border-2 border-green-300 hover:border-green-400 hover:shadow-xl transition-all duration-300 cursor-pointer hover:scale-105 animate-fade-in-up animation-delay-400">
              <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-emerald-400 rounded-xl flex items-center justify-center mb-4 mx-auto shadow-md">
                <i className="ri-book-open-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-black mb-3 text-center text-gray-800">
                <strong>{t('activity3_title')}</strong>
              </h3>
              <p className="text-gray-600 text-center text-sm font-medium">
                {t('activity3_desc')}
              </p>
            </div>

            {/* Activity 4 */}
            <div className="bg-white rounded-2xl p-6 border-2 border-yellow-300 hover:border-yellow-400 hover:shadow-xl transition-all duration-300 cursor-pointer hover:scale-105 animate-fade-in-up animation-delay-500">
              <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-amber-400 rounded-xl flex items-center justify-center mb-4 mx-auto shadow-md">
                <i className="ri-music-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-black mb-3 text-center text-gray-800">
                <strong>{t('activity4_title')}</strong>
              </h3>
              <p className="text-gray-600 text-center text-sm font-medium">
                {t('activity4_desc')}
              </p>
            </div>

            {/* Activity 5 */}
            <div className="bg-white rounded-2xl p-6 border-2 border-purple-300 hover:border-purple-400 hover:shadow-xl transition-all duration-300 cursor-pointer hover:scale-105 animate-fade-in-up">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-violet-400 rounded-xl flex items-center justify-center mb-4 mx-auto shadow-md">
                <i className="ri-camera-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-black mb-3 text-center text-gray-800">
                <strong>{t('activity5_title')}</strong>
              </h3>
              <p className="text-gray-600 text-center text-sm font-medium">
                {t('activity5_desc')}
              </p>
            </div>

            {/* Activity 6 */}
            <div className="bg-white rounded-2xl p-6 border-2 border-orange-300 hover:border-orange-400 hover:shadow-xl transition-all duration-300 cursor-pointer hover:scale-105 animate-fade-in-up animation-delay-200">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-400 to-amber-400 rounded-xl flex items-center justify-center mb-4 mx-auto shadow-md">
                <i className="ri-team-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-black mb-3 text-center text-gray-800">
                <strong>{t('activity6_title')}</strong>
              </h3>
              <p className="text-gray-600 text-center text-sm font-medium">
                {t('activity6_desc')}
              </p>
            </div>

            {/* Activity 7 */}
            <div className="bg-white rounded-2xl p-6 border-2 border-teal-300 hover:border-teal-400 hover:shadow-xl transition-all duration-300 cursor-pointer hover:scale-105 animate-fade-in-up animation-delay-400">
              <div className="w-16 h-16 bg-gradient-to-br from-teal-400 to-cyan-400 rounded-xl flex items-center justify-center mb-4 mx-auto shadow-md">
                <i className="ri-trophy-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-black mb-3 text-center text-gray-800">
                <strong>{t('activity7_title')}</strong>
              </h3>
              <p className="text-gray-600 text-center text-sm font-medium">
                {t('activity7_desc')}
              </p>
            </div>

            {/* Activity 8 */}
            <div className="bg-white rounded-2xl p-6 border-2 border-indigo-300 hover:border-indigo-400 hover:shadow-xl transition-all duration-300 cursor-pointer hover:scale-105 animate-fade-in-up animation-delay-500">
              <div className="w-16 h-16 bg-gradient-to-br from-indigo-400 to-blue-400 rounded-xl flex items-center justify-center mb-4 mx-auto shadow-md">
                <i className="ri-star-smile-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-black mb-3 text-center text-gray-800">
                <strong>{t('activity8_title')}</strong>
              </h3>
              <p className="text-gray-600 text-center text-sm font-medium">
                {t('activity8_desc')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* NEWS FEED SECTION */}
      <section className="py-20 bg-white/80 backdrop-blur-sm relative z-10">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-black mb-4 text-gray-900">
              <strong>📰 Your Feed</strong>
            </h2>
            <p className="text-xl text-gray-700 font-semibold">
              Share your happy moments!
            </p>
          </div>

          {/* Create Post Box */}
          <div className="bg-white rounded-3xl shadow-xl p-6 mb-6 border-4 border-pink-200">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-orange-400 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-user-smile-line text-2xl text-white"></i>
              </div>
              <div className="flex-1">
                <textarea
                  value={newPost}
                  onChange={(e) => setNewPost(e.target.value)}
                  placeholder="What's on your mind? 🤔"
                  className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-2xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-pink-400 focus:border-pink-400 resize-none text-base font-medium"
                  rows={3}
                />
                <div className="flex items-center justify-between mt-4">
                  <div className="flex items-center space-x-3">
                    <button className="flex items-center space-x-2 px-4 py-2 bg-green-100 hover:bg-green-200 rounded-full transition-colors cursor-pointer">
                      <i className="ri-image-line text-green-600 text-lg"></i>
                      <span className="text-green-700 font-bold text-sm">Photo</span>
                    </button>
                    <button className="flex items-center space-x-2 px-4 py-2 bg-blue-100 hover:bg-blue-200 rounded-full transition-colors cursor-pointer">
                      <i className="ri-emotion-happy-line text-blue-600 text-lg"></i>
                      <span className="text-blue-700 font-bold text-sm">Feeling</span>
                    </button>
                  </div>
                  <button
                    onClick={() => {
                      if (newPost.trim()) {
                        alert('Posted: ' + newPost);
                        setNewPost('');
                      }
                    }}
                    className="px-6 py-2 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white rounded-full font-bold text-sm transition-all cursor-pointer whitespace-nowrap shadow-lg"
                  >
                    Post
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Posts Feed */}
          <div className="space-y-6">
            {posts.map((post) => (
              <div key={post.id} className="bg-white rounded-3xl shadow-xl border-4 border-purple-200 overflow-hidden hover:shadow-2xl transition-shadow">
                {/* Post Header */}
                <div className="p-6 pb-4">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 border-2 border-purple-300">
                      <img src={post.avatar} alt={post.author} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-black text-gray-900 text-base">{post.author}</h3>
                      <p className="text-gray-600 text-xs font-medium">{post.time}</p>
                    </div>
                    <button className="w-8 h-8 hover:bg-gray-100 rounded-full flex items-center justify-center transition-colors cursor-pointer">
                      <i className="ri-more-line text-gray-700 text-xl"></i>
                    </button>
                  </div>

                  {/* Post Content */}
                  <p className="text-gray-900 text-base font-medium leading-relaxed mb-4">
                    {post.content}
                  </p>
                </div>

                {/* Post Image */}
                {post.image && (
                  <div className="w-full" style={{ height: '400px' }}>
                    <img src={post.image} alt="Post" className="w-full h-full object-cover object-top" />
                  </div>
                )}

                {/* Post Stats */}
                <div className="px-6 py-3 border-t-2 border-gray-200">
                  <div className="flex items-center justify-between text-sm text-gray-700 font-medium">
                    <span>❤️ {post.likes} likes</span>
                    <span className="text-lg font-black text-transparent bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 bg-clip-text">RESPECT 18</span>
                    <span>💬 {post.comments} comments</span>
                  </div>
                </div>

                {/* Post Actions */}
                <div className="px-6 py-3 border-t-2 border-gray-200">
                  <div className="flex items-center justify-around">
                    <button className="flex items-center space-x-2 px-4 py-2 hover:bg-pink-50 rounded-lg transition-colors cursor-pointer flex-1 justify-center">
                      <i className="ri-heart-line text-pink-500 text-xl"></i>
                      <span className="text-gray-900 font-bold text-sm">Like</span>
                    </button>
                    <button
                      onClick={() => setShowComments({ ...showComments, [post.id]: !showComments[post.id] })}
                      className="flex items-center space-x-2 px-4 py-2 hover:bg-blue-50 rounded-lg transition-colors cursor-pointer flex-1 justify-center"
                    >
                      <i className="ri-chat-3-line text-blue-500 text-xl"></i>
                      <span className="text-gray-900 font-bold text-sm">Comment</span>
                    </button>
                    <button className="flex items-center space-x-2 px-4 py-2 hover:bg-green-50 rounded-lg transition-colors cursor-pointer flex-1 justify-center">
                      <i className="ri-share-forward-line text-green-500 text-xl"></i>
                      <span className="text-gray-900 font-bold text-sm">Share</span>
                    </button>
                  </div>
                </div>

                {/* Comments Section */}
                {showComments[post.id] && (
                  <div className="px-6 py-4 bg-gray-50 border-t-2 border-gray-200">
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-user-smile-line text-white text-sm"></i>
                      </div>
                      <div className="flex-1">
                        <input
                          type="text"
                          placeholder="Write a comment..."
                          className="w-full px-4 py-2 bg-white border-2 border-gray-300 rounded-full text-gray-900 placeholder-gray-500 focus:outline-none focus:border-purple-400 text-sm font-medium"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-yellow-400 via-orange-400 to-pink-400 relative z-10">
        <div className="max-w-2xl mx-auto px-6">
          <div className="bg-white rounded-3xl p-12 shadow-2xl text-center animate-fade-in-up hover:scale-105 transition-transform duration-500">
            <div className="mb-8">
              <div className="w-20 h-20 bg-gradient-to-br from-yellow-400 to-orange-400 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-rocket-line text-4xl text-white"></i>
              </div>
              <h2 className="text-4xl font-bold mb-4 text-gray-900"><strong>{t('cta_title')}</strong></h2>
            </div>
            
            <button
              onClick={() => window.location.href = '/'}
              className="bg-gradient-to-r from-yellow-400 to-orange-400 text-white px-12 py-4 rounded-full font-bold text-lg hover:scale-105 transition-all shadow-xl whitespace-nowrap cursor-pointer"
            >
              {t('cta_button')}
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 bg-green-500 relative z-10">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <img 
            src="https://static.readdy.ai/image/a79563fe53efb1b307a7324e5db6bc2e/126dd8573ffd64afbaee10d6ee258da0.jpeg"
            alt="FaceTrue Logo" 
            className="w-8 h-8 mx-auto mb-4 rounded-full"
          />
          <p className="text-white text-sm mb-2 font-semibold">
            {t('children_footer_copyright')}
          </p>
          <p className="text-teal-50 text-sm mb-4">
            {t('children_footer_desc')}
          </p>
          <a 
            href="https://readdy.ai/?ref=logo" 
            className="text-teal-100 text-xs hover:text-white transition-colors no-underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('footer_powered')}
          </a>
        </div>
      </footer>

      {/* Chat Section */}
      <section className="py-16 bg-white/70 backdrop-blur-sm relative z-10">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-white rounded-3xl shadow-2xl p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-pink-400 to-purple-400 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-chat-smile-3-line text-3xl text-white"></i>
              </div>
              <h2 className="text-3xl font-black text-gray-800 mb-2">
                <strong>Chat with us!</strong>
              </h2>
              <p className="text-gray-600 font-medium">
                Send us a message! 💬
              </p>
            </div>

            {/* Chat Input */}
            <div className="flex items-center space-x-4">
              <div className="flex-1 relative">
                <input
                  type="text"
                  value={chatMessage}
                  onChange={(e) => setChatMessage(e.target.value)}
                  placeholder="Type your message..."
                  className="w-full px-6 py-4 bg-gray-100 border-2 border-gray-200 rounded-full text-gray-800 placeholder-gray-400 focus:outline-none focus:border-pink-400 transition-all text-base font-medium"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && chatMessage.trim()) {
                      alert('Message: ' + chatMessage);
                      setChatMessage('');
                    }
                  }}
                />
                <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center space-x-2">
                  <button className="w-10 h-10 bg-gray-200 hover:bg-gray-300 rounded-full flex items-center justify-center transition-colors cursor-pointer">
                    <i className="ri-emotion-happy-line text-xl text-gray-600"></i>
                  </button>
                </div>
              </div>
              
              <button
                onClick={() => {
                  if (chatMessage.trim()) {
                    alert('Message: ' + chatMessage);
                    setChatMessage('');
                  }
                }}
                className="w-14 h-14 bg-gradient-to-br from-pink-400 to-purple-400 hover:from-pink-500 hover:to-purple-500 rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-all cursor-pointer"
              >
                <i className="ri-send-plane-fill text-2xl text-white"></i>
              </button>
            </div>

            {/* Quick Actions */}
            <div className="mt-6 flex flex-wrap gap-3 justify-center">
              <button className="px-5 py-2 bg-yellow-100 hover:bg-yellow-200 text-yellow-700 rounded-full text-sm font-bold transition-colors cursor-pointer whitespace-nowrap">
                👋 Hello
              </button>
              <button className="px-5 py-2 bg-pink-100 hover:bg-pink-200 text-pink-700 rounded-full text-sm font-bold transition-colors cursor-pointer whitespace-nowrap">
                🎨 Draw
              </button>
              <button className="px-5 py-2 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-full text-sm font-bold transition-colors cursor-pointer whitespace-nowrap">
                🎮 Play games
              </button>
              <button className="px-5 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-full text-sm font-bold transition-colors cursor-pointer whitespace-nowrap">
                📚 Read books
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Activity Modal */}
      {selectedActivity && <ActivityModal activityKey={selectedActivity} />}

      {/* Custom Animation Styles */}
      <style>{`
        @keyframes bounce-slow {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }

        .animate-bounce-slow {
          animation: bounce-slow 3s ease-in-out infinite;
        }

        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out forwards;
        }

        .animation-delay-200 {
          animation-delay: 0.2s;
        }

        .animation-delay-400 {
          animation-delay: 0.4s;
        }

        .animation-delay-500 {
          animation-delay: 0.5s;
        }
      `}</style>
    </div>
  );
}
